import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { SMSPageLayout } from './SMSPageLayout';
import { sendCampaignSms } from '../../services/SmsService';
import { getCurrentUserCredits } from '../../services/CreditsService';
import { PersonalizedRow, PersonalizePreviewRow } from '../../utils/excelPersonalize';

interface RecipientMessage {
  phone: string;
  body: string;
  fields?: Record<string, string>;
}

interface PreviewData {
  message: string;
  recipients: string[];
  campaignName: string;
  scheduledDate: string;
  scheduledTime: string;
  smsCount: number;
  senderId?: string;
  recipientMessages?: RecipientMessage[];
  personalizeColumns?: string[];
  personalizeRows?: PersonalizedRow[];
  personalizePreview?: PersonalizePreviewRow[];
  personalizeFileName?: string;
}

export const SMSPreviewPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const previewData = location.state as PreviewData;
  const [isSending, setIsSending] = useState(false);

  const calculateCost = () => {
    if (previewData.recipientMessages && previewData.recipientMessages.length > 0) {
      return previewData.recipientMessages.reduce(
        (sum, item) => sum + Math.max(1, Math.ceil((item.body || '').length / 160)),
        0
      );
    }
    return previewData.recipients.length * previewData.smsCount;
  };

  const showInsufficientCreditsModal = (required: number, available: number) => {
    const modal = document.createElement('div');
    modal.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50';
    modal.innerHTML = `
      <div class="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <div class="flex items-center justify-center mb-4">
          <svg class="w-12 h-12 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h3 class="text-xl font-semibold text-center mb-2">Insufficient Credits</h3>
        <p class="text-gray-600 text-center mb-4">You do not have enough credits to send this campaign.</p>
        <div class="space-y-2 mb-6 bg-gray-50 p-4 rounded-lg">
          <div class="flex justify-between">
            <span class="text-gray-600">Required Credits:</span>
            <span class="font-medium text-red-600">${required}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-600">Available Credits:</span>
            <span class="font-medium">${available}</span>
          </div>
          <div class="flex justify-between pt-2 border-t">
            <span class="text-gray-700 font-medium">Shortfall:</span>
            <span class="font-medium text-red-600">${required - available}</span>
          </div>
        </div>
        <div class="flex justify-center">
          <button 
            onclick="this.closest('.fixed').remove()"
            class="px-6 py-2 bg-[#FF5722] text-white rounded-md hover:bg-[#F4511E] transition-colors duration-200"
          >
            Close
          </button>
        </div>
      </div>
    `;
    document.body.appendChild(modal);
    
    // Remove modal when clicking outside
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        modal.remove();
      }
    });
  };

  const showSuccessModal = () => {
    const modal = document.createElement('div');
    modal.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50';
    modal.innerHTML = `
      <div class="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <div class="flex items-center justify-center mb-4">
          <svg class="w-12 h-12 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 class="text-xl font-semibold text-center mb-2">Campaign Sent Successfully!</h3>
        <div class="space-y-3 mb-6">
          <div class="flex justify-between">
            <span class="text-gray-600">Campaign Name:</span>
            <span class="font-medium">${previewData.campaignName || 'Untitled'}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-600">Recipients:</span>
            <span class="font-medium">${previewData.recipients.length} contacts</span>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-600">Total SMS:</span>
            <span class="font-medium">${previewData.recipients.length * previewData.smsCount}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-600">Schedule:</span>
            <span class="font-medium">${previewData.scheduledDate && previewData.scheduledTime 
              ? `${previewData.scheduledDate} at ${previewData.scheduledTime}`
              : 'Sent immediately'}</span>
          </div>
        </div>
        <div class="flex justify-center">
          <button 
            onclick="this.closest('.fixed').remove()"
            class="px-6 py-2 bg-[#FF5722] text-white rounded-md hover:bg-[#F4511E] transition-colors duration-200"
          >
            Close
          </button>
        </div>
      </div>
    `;
    document.body.appendChild(modal);
    
    // Remove modal when clicking outside
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        modal.remove();
      }
    });
  };

  const handleSendCampaign = async () => {
    if (isSending) return;
    
    setIsSending(true);
    try {
      // Calculate required credits
      const requiredCredits = calculateCost();
      
      // Check user's available credits
      const userCredits = await getCurrentUserCredits();
      
      if (userCredits.remaining < requiredCredits) {
        showInsufficientCreditsModal(requiredCredits, userCredits.remaining);
        setIsSending(false);
        return;
      }

      // Send the campaign
      const resp = await sendCampaignSms({
        senderId: previewData.senderId,
        body: previewData.message,
        recipients: previewData.recipients,
        recipientMessages: previewData.recipientMessages,
        campaignName: previewData.campaignName || undefined,
        scheduledDate: previewData.scheduledDate || undefined,
        scheduledTime: previewData.scheduledTime || undefined
      });

      // Show success modal
      showSuccessModal();
    } catch (e: any) {
      // Show error modal
      const modal = document.createElement('div');
      modal.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50';
      modal.innerHTML = `
        <div class="bg-white rounded-lg p-6 max-w-md w-full mx-4">
          <div class="flex items-center justify-center mb-4">
            <svg class="w-12 h-12 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 class="text-xl font-semibold text-center mb-2">Failed to Send Campaign</h3>
          <p class="text-gray-600 text-center mb-6">${e?.message || 'An error occurred while sending the campaign. Please try again.'}</p>
          <div class="flex justify-center">
            <button 
              onclick="this.closest('.fixed').remove()"
              class="px-6 py-2 bg-[#FF5722] text-white rounded-md hover:bg-[#F4511E] transition-colors duration-200"
            >
              Close
            </button>
          </div>
        </div>
      `;
      document.body.appendChild(modal);
      modal.addEventListener('click', (e) => {
        if (e.target === modal) {
          modal.remove();
        }
      });
    } finally {
      setIsSending(false);
    }
  };

  if (!previewData) {
    return (
      <SMSPageLayout title="SMS Campaign Preview">
        <div className="text-center py-8">
          <p className="text-gray-600">No preview data available</p>
          <button
            onClick={() => navigate('/sms/campaign')}
            className="mt-4 text-[#FF5722] hover:underline"
          >
            Return to Campaign
          </button>
        </div>
      </SMSPageLayout>
    );
  }

  return (
    <SMSPageLayout title="SMS Campaign Preview">
      <div className="space-y-6">
        {/* Campaign Summary */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">Campaign Summary</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">Campaign Name</p>
              <p className="font-medium">{previewData.campaignName || 'Untitled'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Schedule</p>
              <p className="font-medium">
                {previewData.scheduledDate && previewData.scheduledTime
                  ? `${previewData.scheduledDate} at ${previewData.scheduledTime}`
                  : 'Not scheduled'}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Recipients</p>
              <p className="font-medium">{previewData.recipients.length} contacts</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Total SMS Cost</p>
              <p className="font-medium text-[#FF5722]">
                {previewData.recipients.length * previewData.smsCount} credits
              </p>
            </div>
          </div>
        </div>

        {/* Message Preview */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">Message Preview</h2>
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="whitespace-pre-wrap">{previewData.message}</p>
          </div>
          <div className="mt-2 text-sm text-gray-500">
            {previewData.message.length} characters / 160 characters per SMS
          </div>
          {previewData.recipientMessages && previewData.recipientMessages.length > 0 && (
            <div className="mt-4">
              <h3 className="text-sm font-medium text-gray-700 mb-2">
                Personalized samples (first {Math.min(5, previewData.recipientMessages.length)})
              </h3>
              <div className="space-y-2">
                {previewData.recipientMessages.slice(0, 5).map((item, index) => (
                  <div key={index} className="border border-gray-100 rounded-lg p-3 bg-white">
                    <p className="text-xs text-gray-500 mb-1">{item.phone}</p>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{item.body}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Recipients List */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">Recipients</h2>
          <div className="space-y-2">
            {previewData.recipients.slice(0, 5).map((recipient, index) => (
              <div
                key={index}
                className="py-2 px-3 bg-gray-50 rounded-lg flex justify-between items-center"
              >
                <span className="text-gray-700">{recipient}</span>
                <span className="text-sm text-gray-500">SMS: {previewData.smsCount}</span>
              </div>
            ))}
            {previewData.recipients.length > 5 && (
              <div className="py-2 px-3 text-center text-sm text-gray-500 italic">
                ... and {previewData.recipients.length - 5} more recipient(s)
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-4">
          <button
            onClick={() => navigate('/sms/campaign', { state: previewData })}
            className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors duration-200"
          >
            Back to Campaign
          </button>
          <button
            onClick={handleSendCampaign}
            disabled={isSending}
            className="px-6 py-2 bg-[#FF5722] text-white rounded-md hover:bg-[#F4511E] transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSending ? 'Sending...' : 'Send Campaign'}
          </button>
        </div>
      </div>
    </SMSPageLayout>
  );
};

export default SMSPreviewPage; 